<script>
import { Head, Link } from "@inertiajs/inertia-vue3";
import Layout from "@/Pages/Shared/LayoutDemo.vue";

export default {
    components: {
        Link,
        Head,
    },
    props: {
        allowedDomains: { type: Array, default: () => [] },
        error: { type: String, default: null },
        success: { type: Boolean, default: false },
        email: { type: String, default: null },
    },
    data() {
        return {
            form: this.$inertia.form({
                email: null,
            }),
            showSuccess: false,
            sentEmail: null,
            localError: null,
        };
    },
    mounted() {
        // Sadece POST sonrası success göster
        if (this.success && this.email) {
            this.showSuccess = true;
            this.sentEmail = this.email;
        }
    },
    methods: {
        validateEmailDomain() {
            if (!this.form.email) {
                return "Email adresi gereklidir.";
            }
            const emailDomain = this.form.email.substring(this.form.email.lastIndexOf("@")).toLowerCase();
            // @kiralabunu.com her zaman izinlidir; listede gösterilmez
            if (emailDomain === "@kiralabunu.com") {
                return null;
            }
            if (this.allowedDomains.length === 0) {
                return "Bu sistem sadece izin verilen domain'li email adresleriyle kullanılabilir.";
            }
            if (!this.allowedDomains.map((d) => d.toLowerCase()).includes(emailDomain)) {
                return "Bu sistem sadece izin verilen domain'li email adresleriyle kullanılabilir.";
            }
            return null;
        },
        submit() {
            this.localError = null;

            // Frontend validasyonu
            const validationError = this.validateEmailDomain();
            if (validationError) {
                this.localError = validationError;
                return;
            }

            this.form.post("/email-davet/gonder", {
                preserveScroll: false,
                onSuccess: (page) => {
                    if (page.props.success) {
                        this.showSuccess = true;
                        this.sentEmail = this.form.email;
                    }
                },
            });
        },
        resetForm() {
            this.showSuccess = false;
            this.sentEmail = null;
            this.form.reset();
            // URL'i temizle
            this.$inertia.visit("/email-davet", { preserveState: false });
        },
    },
    layout: Layout,
};
</script>

<template>
    <Head title="Email Daveti" />

    <main class="my-6">
        <section class="mt-6">
            <div class="flex mx-auto flex-col max-w-[350px] md:max-w-lg py-8 rounded-2lg bg-white border border-bordergray px-[2%]">
                <!-- Başarı Mesajı -->
                <div v-if="showSuccess" class="text-center">
                    <div class="mb-6">
                        <svg class="mx-auto w-16 h-16 text-kbgreen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                        </svg>
                    </div>
                    <div class="font-bold text-xl text-kbgreen mb-4">Email Gönderildi!</div>
                    <p class="text-gray-600 mb-6">
                        <strong>{{ sentEmail }}</strong> adresine davet linki gönderildi.
                    </p>
                    <p class="text-gray-500 text-sm mb-6">Email kutunuzu kontrol edin. Link 24 saat boyunca geçerli olacaktır.</p>
                    <button @click.prevent="resetForm" class="text-kb-light-blue text-sm hover:underline cursor-pointer bg-transparent border-none">Yeni davet talep et</button>
                </div>

                <!-- Form -->
                <form v-else @submit.prevent="submit">
                    <div class="font-bold text-xl mb-2">Kiralabunu sistemine giriş için email adresinize bir davet linki göndereceğiz.</div>
                    <p class="text-gray-500 text-sm mb-6">
                        Sadece şirket email adresinizle giriş yapabilirsiniz.
                    </p>

                    <div class="w-full">
                        <input id="email" type="email" v-model="form.email" placeholder="Şirket email adresiniz" autocomplete="off" class="w-full border rounded-lg border-bordergray hover:border-acordion-green focus:border-acordion-green focus:outline-none focus:ring-kbgreen p-3" required />
                    </div>

                    <!-- Hata Mesajı -->
                    <div v-if="error || localError" class="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div class="flex items-start">
                            <svg class="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                            </svg>
                            <div>
                                <p class="text-red-700 text-sm font-medium">Gönderim başarısız</p>
                                <p class="text-red-600 text-sm mt-1">{{ localError || error }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="flex mt-6 w-full self-center">
                        <button class="bg-kbgreen text-white rounded-full py-3 px-4 self-center font-bold w-full text-center hover:shadow-acordion-green disabled:opacity-50" type="submit" :disabled="form.processing">
                            <span v-if="form.processing">Gönderiliyor...</span>
                            <span v-else>Giriş Linki Gönder</span>
                        </button>
                    </div>

                    <!-- İzin Verilen Domainler -->
                    <div v-if="allowedDomains.length > 0" class="mt-4 text-center">
                        <span class="text-kbgreen font-medium cursor-help relative inline-flex items-center group">
                            <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
                            </svg>
                            İzin verilen domainler
                            <!-- Tooltip -->
                            <span class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-2 px-3 whitespace-nowrap z-10">
                                {{ allowedDomains.join(', ') }}
                            </span>
                        </span>
                    </div>
                </form>
            </div>
        </section>
    </main>
</template>
